import { useState, useRef, useEffect } from 'react'
import { Modal } from '@/shared/components/Modal'
import { Tooltip } from '@/shared/components/Tooltip'
import { useSidebar } from '@/context/SidebarContext'
import { api } from '@/lib/api'
import { trackEvent } from '@/lib/telemetry'
import { styles } from '@/shell/layout.styles'
import { NAV_ICONS } from '@/shell/nav/navIcons'

export function AuditModal({ onClose }: { onClose: () => void }) {
  // Same self-contained pattern as DriftDetectionModal: interest fires on the
  // explicit "Register interest" click (never on open), feedback is inline
  // because a toast can't paint over a native <dialog>.
  const [state, setState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = useState('')
  const mountedRef = useRef(true)
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false } }, [])

  async function register() {
    setState('submitting')
    setErrorMsg('')
    try {
      trackEvent('register_interest', 'audit')
      await api.post('/api/interest/audit')
      if (mountedRef.current) setState('success')
    } catch (err) {
      if (!mountedRef.current) return
      setState('error')
      setErrorMsg(err instanceof Error ? err.message : 'Network error. Please try again.')
    }
  }

  return (
    <Modal track="audit" title="Audit" kicker="Coming soon" onClose={onClose} maxWidth={720}>
      <p className="guide-para">
        When something breaks, the first question is always the same - <em>who changed
        what, and when?</em> Today that answer is scattered across CI logs, cloud
        trails, and memory. Audit will be the single, tamper-evident timeline for
        everything that moves through your org.
      </p>
      <div style={{ fontFamily: 'var(--mono)', fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 6 }}>
        How it would work
      </div>
      <ul className="guide-list">
        <li><strong>Every artefact movement</strong> - who pushed or pulled which module, provider, plan or state file, from which workspace, pipeline, or token, and when.</li>
        <li><strong>Org changes, attributed</strong> - policies edited, overrides granted, tokens issued or revoked, members added or removed, serve modes and version overrides changed - each tied to the person or bot that did it.</li>
        <li><strong>Full context, not just a verb</strong> - before/after values, the source IP or CI run, and the workspace it touched, so an entry stands on its own without spelunking elsewhere.</li>
        <li><strong>Filter and export</strong> - slice by actor, artefact, workspace, or time window; stream to your SIEM or export for a compliance evidence pack (SOC 2 / ISO 27001).</li>
        <li><strong>Tamper-evident</strong> - append-only with integrity you can prove, so the record holds up when an auditor - or an incident - comes asking.</li>
      </ul>
      <div className="callout-accent">
        <strong>Exploratory.</strong> This isn't built yet. Register your interest so we
        can prioritise it.
      </div>
      {state === 'success' ? (
        <div style={{ marginTop: 16 }}>
          <div className="feedback-success">Thanks - we'll prioritise it.</div>
          <div className="modal-actions">
            <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
          </div>
        </div>
      ) : (
        <>
          {state === 'error' && (
            <div className="feedback-error" style={{ marginTop: 10 }}>{errorMsg}</div>
          )}
          <div className="modal-actions">
            <button className="btn btn-sm" onClick={onClose}>Close</button>
            <button className="btn btn-primary btn-sm" onClick={register} disabled={state === 'submitting'}>
              {state === 'submitting' ? 'Registering…' : 'Register interest'}
            </button>
          </div>
        </>
      )}
    </Modal>
  )
}

export function AuditNavButton() {
  const { collapsed } = useSidebar()
  const [hovered, setHovered] = useState(false)
  const [show, setShow] = useState(false)

  const open = () => setShow(true)

  return (
    <>
      {show && <AuditModal onClose={() => setShow(false)} />}
      <Tooltip text="A tamper-evident timeline of who uploaded, downloaded, or changed what across your org" placement="right">
        <button
          style={{ ...styles.navItem, ...(collapsed ? styles.navItemCollapsed : {}), ...(hovered ? styles.navItemHover : {}), width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
          onClick={open}
          aria-label={collapsed ? 'Audit' : undefined}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {NAV_ICONS.audit}
          {!collapsed && 'Audit'}
        </button>
      </Tooltip>
    </>
  )
}

